import { useEffect, useRef, useState, useCallback } from "react";

const FRILL_CONTAINER_KEY = "container_2eZIAl_ZiUB5t";
const FRILL_WIDGET_KEY = "widget_61L9iO_nOlFqk";

export function useFrillWidget() {
  const widgetInstanceRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isContainerReady, setIsContainerReady] = useState(false);
  const isInitializingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || hasInitializedRef.current) return;

    if (!window.Frill) {
      let f = 0;
      const i = {};
      window.Frill = function (e, o) {
        let n;
        const l = f++;
        const c = new Promise(function (v, d) {
          i[l] = {
            params: [e, o],
            resolve(p) {
              n = p;
              v(p);
            },
            reject: d,
          };
        });
        c.destroy = function () {
          delete i[l];
          n && n.destroy();
        };
        return c;
      };
      window.Frill.q = i;
    }

    const existingScript = document.querySelector(
      'script[src*="widget.frill.co"]'
    );
    if (!existingScript) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = "https://widget.frill.co/v2/container.js";
      document.body.appendChild(script);
    }

    const initContainer = async () => {
      try {
        await window.Frill("container", {
          key: FRILL_CONTAINER_KEY,
        });
        hasInitializedRef.current = true;
        setIsContainerReady(true);
      } catch (err) {
        console.error("Failed to initialize Frill container:", err);
      }
    };

    const timer = setTimeout(initContainer, 300);
    return () => clearTimeout(timer);
  }, []);

  const initializeWidget = useCallback(async () => {
    if (widgetInstanceRef.current || isInitializingRef.current) {
      return widgetInstanceRef.current;
    }

    if (!window.Frill) {
      throw new Error("Frill not loaded");
    }

    try {
      isInitializingRef.current = true;
      const widget = await window.Frill("widget", {
        key: FRILL_WIDGET_KEY,
        callbacks: {
          onBadgeCount({ announcements, count }) {
            setUnreadCount(count);
          },
          onOpen() {
            setIsOpen(true);
          },
          onClose() {
            setIsOpen(false);
          },
        },
      });

      widgetInstanceRef.current = widget;

      return widget;
    } catch (err) {
      console.error("Failed to initialize widget:", err);
      throw err;
    } finally {
      isInitializingRef.current = false;
    }
  }, []);

  // Eagerly initialize the widget (without opening) once the container is ready
  useEffect(() => {
    if (!isContainerReady) return;
    // Initialize in background to receive badge count updates early
    initializeWidget().catch((err) => {
      // Non-blocking: just log so it doesn't affect the UI
      console.error("Background widget init failed:", err);
    });
    // Only run once when container becomes ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContainerReady]);

  // Open widget
  const openWidget = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let widget = widgetInstanceRef.current;

      if (!widget) {
        widget = await initializeWidget();
      }

      if (!widget) {
        throw new Error("Failed to get widget instance");
      }

      // Wait a bit to ensure widget is ready
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (widget.open) {
        await widget.open();
      }

      setIsLoading(false);
    } catch (err) {
      console.error("Error opening widget:", err);
      setError("Failed to load announcements. Please try again.");
      setIsLoading(false);
    }
  }, [initializeWidget]);

  // Close widget
  const closeWidget = useCallback(() => {
    const widget = widgetInstanceRef.current;
    if (widget && widget.close) {
      widget.close();
    }
  }, []);

  // Toggle widget
  const toggleWidget = useCallback(() => {
    if (isOpen) {
      closeWidget();
    } else {
      openWidget();
    }
  }, [isOpen, openWidget, closeWidget]);

  return {
    isOpen,
    isLoading,
    error,
    unreadCount,
    hasNewAnnouncements: unreadCount > 0,
    openWidget,
    closeWidget,
    toggleWidget,
  };
}
